<script setup lang="ts">
import {
  ArrowRight,
  BadgeCheck,
  Clock3,
  MessageCircle,
  Search,
  ShieldCheck,
  Star,
} from "lucide-vue-next";

const localeRoute = useLocaleRoute();
const { t } = useI18n();

const benefits = [
  {
    icon: Search,
    titleKey: "search.ads.professionals.sidebar.benefits.enquiries.title",
    descriptionKey:
      "search.ads.professionals.sidebar.benefits.enquiries.description",
  },
  {
    icon: ShieldCheck,
    titleKey: "search.ads.professionals.sidebar.benefits.reputation.title",
    descriptionKey:
      "search.ads.professionals.sidebar.benefits.reputation.description",
  },
  {
    icon: MessageCircle,
    titleKey: "search.ads.professionals.sidebar.benefits.direct_contact.title",
    descriptionKey:
      "search.ads.professionals.sidebar.benefits.direct_contact.description",
  },
] as const;
</script>

<template>
  <aside class="hidden min-[1360px]:block min-[1360px]:w-[300px]">
    <div class="sticky top-[156px]">
      <section
        class="overflow-hidden rounded-[24px] border border-[#DDE6E2] bg-white shadow-[0_18px_48px_rgba(17,30,25,0.05)]"
      >
        <div class="select-none px-3 pt-3">
          <div
            class="relative h-[296px] overflow-hidden rounded-[22px] bg-[#F7F4EE] shadow-[0_12px_32px_rgba(17,30,25,0.07)]"
            aria-hidden="true"
          >
            <img
              src="/images/search/promo-profile-background.webp"
              alt=""
              class="absolute inset-0 h-full w-full scale-[1.035] object-cover object-[56%_56%]"
              loading="lazy"
              draggable="false"
            />
            <div
              class="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.18)_0%,rgba(255,255,255,0.02)_52%,rgba(255,255,255,0.16)_100%)]"
            />

            <!-- Main profile card -->
            <div
              class="absolute left-1/2 top-[12px] z-10 w-[196px] -translate-x-1/2 overflow-hidden rounded-[20px] border-[2px] border-white bg-white shadow-[0_22px_46px_rgba(17,30,25,0.16)]"
            >
              <div class="relative h-[178px] overflow-hidden bg-[#F1F3F1]">
                <img
                  src="/images/search/promo-specialist-painter.webp"
                  alt=""
                  class="h-full w-full object-cover object-[center_10%]"
                  loading="lazy"
                  draggable="false"
                />
              </div>

              <div class="px-3.5 pb-3 pt-2.5">
                <div
                  class="truncate text-sm font-bold leading-tight text-app-text"
                >
                  {{ t("search.ads.professionals.sidebar.profile_name") }}
                </div>
                <div class="mt-1 truncate text-xs font-medium text-app-muted">
                  {{ t("search.ads.professionals.sidebar.category") }} ·
                  {{ t("search.ads.professionals.sidebar.city") }}
                </div>
                <div
                  class="mt-2 flex items-center gap-1.5 text-xs font-semibold text-app-text"
                >
                  <Star
                    class="size-[16px] fill-current text-primary"
                    :stroke-width="1.7"
                  />
                  <span>{{
                    t("search.ads.professionals.sidebar.rating")
                  }}</span>
                  <span class="text-[#B6C2BC]">·</span>
                  <span class="truncate font-medium text-app-muted">
                    {{ t("search.ads.professionals.sidebar.reviews") }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Trust badge -->
            <div
              class="absolute left-[5px] top-[80px] z-20 w-[90px] rounded-[17px] border border-[#E0E8E4] bg-white px-3 py-3 shadow-[0_14px_30px_rgba(17,30,25,0.11)]"
            >
              <BadgeCheck
                class="size-[18px] text-primary"
                :stroke-width="1.9"
              />
              <div class="mt-2 text-xs font-bold leading-[1.15] text-app-text">
                {{ t("search.ads.professionals.sidebar.verified") }}
              </div>
              <div class="mt-1 text-xs leading-tight text-app-muted">
                {{ t("search.ads.professionals.sidebar.verified_meta") }}
              </div>
            </div>

            <!-- Rating badge -->
            <div
              class="absolute right-[5px] top-[112px] z-20 w-[92px] rounded-[17px] border border-[#E0E8E4] bg-white px-3 py-3 shadow-[0_14px_30px_rgba(17,30,25,0.11)]"
            >
              <div class="flex items-center gap-2">
                <Star class="size-[18px] text-primary" :stroke-width="1.9" />
                <span class="text-base font-bold leading-none text-app-text">
                  {{ t("search.ads.professionals.sidebar.rating") }}
                </span>
              </div>
              <div class="mt-2 text-xs leading-tight text-app-muted">
                {{ t("search.ads.professionals.sidebar.reviews") }}
              </div>
            </div>
          </div>
        </div>

        <div class="select-text px-5 pb-[14px] pt-[22px]">
          <h2
            class="font-gabarito text-base font-bold leading-[1.12] tracking-[-0.028em] text-app-text"
          >
            {{ t("search.ads.professionals.sidebar.title") }}
          </h2>

          <p class="mt-2 text-xs leading-[1.44] text-app-muted">
            {{ t("search.ads.professionals.sidebar.description") }}
          </p>

          <div class="mt-3.5 divide-y divide-[#EDF1EF]">
            <div
              v-for="benefit in benefits"
              :key="benefit.titleKey"
              class="flex gap-2.5 py-[9px] first:pt-0"
            >
              <span
                class="flex size-[34px] shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary"
              >
                <component
                  :is="benefit.icon"
                  class="size-[16px]"
                  :stroke-width="1.85"
                />
              </span>
              <span class="min-w-0 pt-px">
                <span
                  class="block text-xs font-bold leading-[1.25] text-app-text"
                >
                  {{ t(benefit.titleKey) }}
                </span>
                <span class="mt-0.5 block text-xs leading-[1.4] text-app-muted">
                  {{ t(benefit.descriptionKey) }}
                </span>
              </span>
            </div>
          </div>

          <NuxtLink
            :to="localeRoute('/register')"
            class="mt-3 flex h-[43px] w-full items-center justify-center gap-2 rounded-[12px] bg-primary px-3 text-xs font-semibold text-white transition duration-300 hover:bg-primary-400"
          >
            <span class="whitespace-nowrap">
              {{ t("search.ads.professionals.sidebar.action") }}
            </span>
            <ArrowRight class="size-4 shrink-0" :stroke-width="2" />
          </NuxtLink>

          <div
            class="mt-2 flex items-center justify-center gap-1.5 text-center text-xs font-medium leading-4 text-app-muted"
          >
            <Clock3
              class="size-3.5 shrink-0 text-primary"
              :stroke-width="1.9"
            />
            <span>{{ t("search.ads.professionals.sidebar.note") }}</span>
          </div>
        </div>
      </section>
    </div>
  </aside>
</template>
